
import React, { createContext, useContext, useState, ReactNode, useCallback, useMemo, useEffect } from 'react';
import { MockGooglePlace } from '@/utils/mockGoogleMapsApi';
import { BusinessDetails as FullBusinessDetails } from '@/components/business/addBusinessModal/types';
import { useBusinessFormValidation } from '@/components/business/firstBusiness/hooks/useBusinessFormValidation';
import { getIndustryById } from '@/data/industries'; // Added import

type FormSection = 'search' | 'form';

// Use the more comprehensive BusinessDetails type
type BusinessDetails = FullBusinessDetails;

interface BusinessFormContextType {
  formSection: FormSection;
  setFormSection: React.Dispatch<React.SetStateAction<FormSection>>;
  businessDetails: BusinessDetails;
  setBusinessDetails: React.Dispatch<React.SetStateAction<BusinessDetails>>; // Keep for direct updates if needed
  selectedBusiness: MockGooglePlace | null;
  setSelectedBusiness: React.Dispatch<React.SetStateAction<MockGooglePlace | null>>;
  
  // Input Handlers
  handleInputChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  handleSelectChange: (value: string, field: string) => void;
  handleDateSelect: (date: Date | undefined) => void;
  handleWebsiteBlur: () => void;
  handleEmailChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleTermsChange: (checked: boolean) => void;
  handleMarketingChange: (checked: boolean) => void;

  // UI State (Open/Closed, Calendar)
  calendarOpen: boolean;
  setCalendarOpen: (open: boolean) => void;
  selectedDate?: Date;
  // setSelectedDate: React.Dispatch<React.SetStateAction<Date | undefined>>; // Handled by handleDateSelect

  basicInfoOpen: boolean;
  setBasicInfoOpen: (open: boolean) => void;
  addressOpen: boolean;
  setAddressOpen: (open: boolean) => void;
  industryOpen: boolean;
  setIndustryOpen: (open: boolean) => void;
  userDetailsOpen: boolean;
  setUserDetailsOpen: (open: boolean) => void;

  // User Specific Data
  userEmail: string;
  // setUserEmail: React.Dispatch<React.SetStateAction<string>>; // Handled by handleEmailChange
  termsAccepted: boolean;
  // setTermsAccepted: React.Dispatch<React.SetStateAction<boolean>>; // Handled by handleTermsChange
  marketingOptIn: boolean;
  // setMarketingOptIn: React.Dispatch<React.SetStateAction<boolean>>; // Handled by handleMarketingChange

  // Validation/Completion Status (from useBusinessFormValidation)
  basicInfoComplete: boolean;
  addressComplete: boolean;
  industryComplete: boolean;
  userDetailsComplete: boolean;
  isSubmitEnabled: boolean;

  // Edit Status
  basicInfoEdited: boolean;
  setBasicInfoEdited: React.Dispatch<React.SetStateAction<boolean>>;
  addressEdited: boolean;
  setAddressEdited: React.Dispatch<React.SetStateAction<boolean>>;
  industryEdited: boolean;
  setIndustryEdited: React.Dispatch<React.SetStateAction<boolean>>;
  // userDetailsEdited: boolean; // UserDetails section doesn't have a save button in original props
  // setuserDetailsEdited: React.Dispatch<React.SetStateAction<boolean>>;


  // Change Tracking for Save Buttons
  basicInfoHasChanges: boolean;
  setBasicInfoHasChanges: React.Dispatch<React.SetStateAction<boolean>>;
  addressHasChanges: boolean;
  setAddressHasChanges: React.Dispatch<React.SetStateAction<boolean>>;
  industryHasChanges: boolean;
  setIndustryHasChanges: React.Dispatch<React.SetStateAction<boolean>>;

  // Save Handlers
  saveBasicInfo: () => void;
  saveAddress: () => void;
  saveIndustry: () => void;

  // Errors
  errors: { [key: string]: string | undefined }; // General errors object
  setErrors: React.Dispatch<React.SetStateAction<{ [key: string]: string | undefined }>>;

  // Specific UI state for BasicInfoSection
  shortNameManuallyEdited: boolean;
  setShortNameManuallyEdited: React.Dispatch<React.SetStateAction<boolean>>;

  // Loading states for save operations
  isSavingBasicInfo: boolean;
  isSavingAddress: boolean;
  isSavingIndustry: boolean;
}

const defaultBusinessDetails: BusinessDetails = {
  name: '',
  shortName: '',
  phone: '',
  email: '',
  industry: '',
  industryVertical: '',
  address: '',
  city: '',
  state: '',
  zip: '',
  website: '',
  foundingDate: undefined,
  founded: undefined,
  timeZone: '',
  coordinates: undefined,
  // handleCoordinatesUpdate and handleEnsureTimeZone are methods, not typically part of default state
};

const BusinessFormContext = createContext<BusinessFormContextType | undefined>(undefined);

export const useBusinessFormContext = () => {
  const context = useContext(BusinessFormContext);
  if (!context) {
    throw new Error('useBusinessFormContext must be used within a BusinessFormProvider');
  }
  return context;
};

interface BusinessFormProviderProps {
  children: ReactNode;
}

export const BusinessFormProvider: React.FC<BusinessFormProviderProps> = ({ children }) => {
  const [formSection, setFormSection] = useState<FormSection>('search');
  const [businessDetails, setBusinessDetails] = useState<BusinessDetails>(defaultBusinessDetails);
  const [selectedBusiness, setSelectedBusiness] = useState<MockGooglePlace | null>(null);

  // UI State
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(undefined);
  const [basicInfoOpen, setBasicInfoOpen] = useState(true);
  const [addressOpen, setAddressOpen] = useState(false);
  const [industryOpen, setIndustryOpen] = useState(false);
  const [userDetailsOpen, setUserDetailsOpen] = useState(false);

  // User Specific Data
  const [userEmail, setUserEmail] = useState('');
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [marketingOptIn, setMarketingOptIn] = useState(false);

  // Edit Status
  const [basicInfoEdited, setBasicInfoEdited] = useState(false);
  const [addressEdited, setAddressEdited] = useState(false);
  const [industryEdited, setIndustryEdited] = useState(false);

  // Change Tracking
  const [basicInfoHasChanges, setBasicInfoHasChanges] = useState(false);
  const [addressHasChanges, setAddressHasChanges] = useState(false);
  const [industryHasChanges, setIndustryHasChanges] = useState(false);
  const [shortNameManuallyEdited, setShortNameManuallyEdited] = useState(false);
  
  // Errors
  const [errors, setErrors] = useState<{ [key: string]: string | undefined }>({});

  // Loading states
  const [isSavingBasicInfo, setIsSavingBasicInfo] = useState(false);
  const [isSavingAddress, setIsSavingAddress] = useState(false);
  const [isSavingIndustry, setIsSavingIndustry] = useState(false);

  // Integrate useBusinessFormValidation
  const {
    basicInfoComplete,
    addressComplete,
    industryComplete,
    userDetailsComplete,
    isSubmitEnabled,
  } = useBusinessFormValidation(businessDetails, userEmail, termsAccepted, setErrors);

  // Handlers
  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    
    setBusinessDetails(prev => {
      const newDetails = { ...prev, [name]: value };
      
      // Auto-update shortName based on name, if not manually edited
      if (name === 'name' && !shortNameManuallyEdited) {
        if (value.length > 30) {
          newDetails.shortName = value.substring(0, 30);
        } else {
          newDetails.shortName = value;
        }
      }
      return newDetails;
    });

    // If shortName is directly edited, mark as manually edited
    if (name === 'shortName') {
      setShortNameManuallyEdited(true);
    }

    // Set corresponding 'hasChanges' flag
    if (basicInfoOpen) setBasicInfoHasChanges(true);
    if (addressOpen) setAddressHasChanges(true);
    // Industry changes are mostly select, handled by handleSelectChange
    // UserDetails changes are handled by their specific handlers
  }, [basicInfoOpen, addressOpen, shortNameManuallyEdited, setShortNameManuallyEdited, setBusinessDetails]);

  const handleSelectChange = useCallback((value: string, field: string) => {
    if (field === 'industry') {
      const industryObject = getIndustryById(value);
      setBusinessDetails(prev => ({
        ...prev,
        industryId: value, // Store the ID in industryId
        industry: industryObject ? industryObject.name : '', // Store the name in industry
      }));
      if (industryOpen) setIndustryHasChanges(true);
    } else {
      setBusinessDetails(prev => ({ ...prev, [field]: value }));
      if (basicInfoOpen && (field === 'timeZone' /* Add other basic info select fields if any */)) setBasicInfoHasChanges(true);
      if (addressOpen && (field === 'state' /* Add other address select fields if any */)) setAddressHasChanges(true);
    }
  }, [basicInfoOpen, addressOpen, industryOpen, setBusinessDetails, setIndustryHasChanges]);

  const handleDateSelect = useCallback((date: Date | undefined) => {
    setSelectedDate(date);
    setBusinessDetails(prev => ({ ...prev, foundingDate: date ? date.toISOString().split('T')[0] : undefined }));
    if (basicInfoOpen) setBasicInfoHasChanges(true);
  }, [basicInfoOpen]);

  const handleWebsiteBlur = useCallback(() => {
    // console.log('Website field blurred');
    // Add validation or formatting if needed
  }, []);

  const handleEmailChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setUserEmail(e.target.value.trim()); // Trim the email value
    // UserDetails section doesn't have a "save" button, changes are immediate for validation
  }, []);

  const handleTermsChange = useCallback((checked: boolean) => {
    setTermsAccepted(checked);
  }, []);

  const handleMarketingChange = useCallback((checked: boolean) => {
    setMarketingOptIn(checked);
  }, []);

  // Save Handlers
  const saveBasicInfo = useCallback(async () => {
    if (!basicInfoComplete) {
      console.warn("Basic Info not complete. Save aborted.");
      return;
    }
    setIsSavingBasicInfo(true);
    console.log('Saving Basic Info:', businessDetails);
    try {
      await new Promise(resolve => setTimeout(resolve, 1000)); // Simulate API call
      setBasicInfoEdited(true);
      setBasicInfoHasChanges(false);
      setBasicInfoOpen(false);
      setAddressOpen(true);
    } catch (error) {
      console.error("Error saving basic info:", error);
      // TODO: Set error state in context if needed
    } finally {
      setIsSavingBasicInfo(false);
    }
  }, [
    businessDetails, basicInfoComplete, setBasicInfoEdited, setBasicInfoHasChanges, 
    setBasicInfoOpen, setAddressOpen, setIsSavingBasicInfo
  ]);

  const saveAddress = useCallback(async () => {
    if (!addressComplete) {
      console.warn("Address not complete. Save aborted.");
      return;
    }
    setIsSavingAddress(true);
    console.log('Saving Address:', businessDetails);
    try {
      await new Promise(resolve => setTimeout(resolve, 1000)); // Simulate API call
      setAddressEdited(true);
      setAddressHasChanges(false);
      setAddressOpen(false);
      setIndustryOpen(true);
    } catch (error) {
      console.error("Error saving address:", error);
    } finally {
      setIsSavingAddress(false);
    }
  }, [
    businessDetails, addressComplete, setAddressEdited, setAddressHasChanges,
    setAddressOpen, setIndustryOpen, setIsSavingAddress
  ]);

  const saveIndustry = useCallback(async () => {
    if (!industryComplete) {
      console.warn("Industry not complete. Save aborted.");
      return;
    }
    setIsSavingIndustry(true);
    console.log('Saving Industry:', businessDetails);
    try {
      await new Promise(resolve => setTimeout(resolve, 1000)); // Simulate API call
      setIndustryEdited(true);
      setIndustryHasChanges(false);
      setIndustryOpen(false);
      setUserDetailsOpen(true);
    } catch (error) {
      console.error("Error saving industry:", error);
    } finally {
      setIsSavingIndustry(false);
    }
  }, [
    businessDetails, industryComplete, setIndustryEdited, setIndustryHasChanges,
    setIndustryOpen, setUserDetailsOpen, setIsSavingIndustry
  ]);


  // Use console.log outside of useEffect to avoid infinite renders
  // console.log("BusinessFormContext: Current formSection:", formSection);
  // console.log("BusinessFormContext: Current businessDetails:", businessDetails);

  const value = useMemo(() => ({
    formSection,
    setFormSection,
    businessDetails,
    setBusinessDetails, // Exposing setBusinessDetails for flexibility, e.g., when a business is selected from search
    selectedBusiness,
    setSelectedBusiness,
    handleInputChange,
    handleSelectChange,
    handleDateSelect,
    handleWebsiteBlur,
    handleEmailChange,
    handleTermsChange,
    handleMarketingChange,
    calendarOpen,
    setCalendarOpen,
    selectedDate,
    basicInfoOpen,
    setBasicInfoOpen,
    addressOpen,
    setAddressOpen,
    industryOpen,
    setIndustryOpen,
    userDetailsOpen,
    setUserDetailsOpen,
    userEmail,
    termsAccepted,
    marketingOptIn,
    // Ensure these are explicitly boolean for the context type
    basicInfoComplete: !!basicInfoComplete, // from useBusinessFormValidation
    addressComplete: !!addressComplete,   // from useBusinessFormValidation
    industryComplete: !!industryComplete, // from useBusinessFormValidation
    userDetailsComplete: !!userDetailsComplete, // from useBusinessFormValidation
    isSubmitEnabled: !!isSubmitEnabled,     // from useBusinessFormValidation
    basicInfoEdited,
    setBasicInfoEdited,
    addressEdited,
    setAddressEdited,
    industryEdited,
    setIndustryEdited,
    basicInfoHasChanges,
    setBasicInfoHasChanges,
    addressHasChanges,
    setAddressHasChanges,
    industryHasChanges,
    setIndustryHasChanges,
    saveBasicInfo,
    saveAddress,
    saveIndustry,
    errors,
    setErrors,
    shortNameManuallyEdited,
    setShortNameManuallyEdited,
    isSavingBasicInfo,
    isSavingAddress,
    isSavingIndustry,
  }), [
    formSection, businessDetails, selectedBusiness,
    handleInputChange, handleSelectChange, handleDateSelect, handleWebsiteBlur,
    handleEmailChange, handleTermsChange, handleMarketingChange,
    calendarOpen, selectedDate,
    basicInfoOpen, addressOpen, industryOpen, userDetailsOpen,
    userEmail, termsAccepted, marketingOptIn, 
    // The completion flags are part of the dependencies for the save handlers indirectly
    // because they influence whether the save buttons are enabled.
    // Explicitly adding them to dependency arrays of save handlers if they were used inside.
    // For now, the save handlers assume completion is met if they are called.
    basicInfoComplete, addressComplete, industryComplete, userDetailsComplete, isSubmitEnabled,
    basicInfoEdited, addressEdited, industryEdited, 
    basicInfoHasChanges, addressHasChanges, industryHasChanges,
    saveBasicInfo, saveAddress, saveIndustry, // These are useCallback deps
    errors, shortNameManuallyEdited,
    // Also add setters used in save handlers to their dependency arrays
    setBasicInfoOpen, setAddressOpen, setIndustryOpen, setUserDetailsOpen, 
    setShortNameManuallyEdited, // Added to dependency array for handleInputChange
    // Add loading states to dependency array
    isSavingBasicInfo, isSavingAddress, isSavingIndustry
  ]);

  return (
    <BusinessFormContext.Provider value={value}>
      {children}
    </BusinessFormContext.Provider>
  );
};
